/**
 * Request Cache Service
 * 
 * Implements HTTP caching with proper cache headers to reduce redundant requests.
 * 
 * Based on HAR analysis findings:
 * - 141 requests (52%) lack proper cache headers
 * - Many duplicate requests to same endpoints
 * - Need to implement client-side caching
 */

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  etag?: string;
}

export interface CacheOptions {
  ttl?: number;              // Time to live in ms (default: 5 minutes)
  maxSize?: number;           // Max cache entries (default: 100)
  useEtag?: boolean;          // Use ETag for validation (default: true)
}

class RequestCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: Required<CacheOptions> = {
    ttl: 5 * 60 * 1000,       // 5 minutes
    maxSize: 100,
    useEtag: true,
  };

  constructor(options?: CacheOptions) {
    if (options) {
      this.config = { ...this.config, ...options };
    }
  }

  /**
   * Get cached data if available and not expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set cache entry
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Enforce max size
    if (this.cache.size >= this.config.maxSize) {
      // Remove oldest entry
      const oldestKey = this.findOldestKey();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    const now = Date.now();
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + (ttl || this.config.ttl),
    };

    this.cache.set(key, entry);
  }

  /**
   * Set cache entry with ETag
   */
  setWithEtag<T>(key: string, data: T, etag: string, ttl?: number): void {
    this.set(key, data, ttl);
    const entry = this.cache.get(key);
    if (entry) {
      entry.etag = etag;
    }
  }

  /**
   * Get ETag for a cached entry
   */
  getEtag(key: string): string | undefined {
    const entry = this.cache.get(key);
    return entry?.etag;
  }

  /**
   * Check if key exists and is valid
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) {
      return false;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Invalidate cache entry
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string | RegExp): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    const keysToDelete: string[] = [];

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clean expired entries
   */
  cleanExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    entries: Array<{ key: string; age: number; expiresIn: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: now - entry.timestamp,
      expiresIn: entry.expiresAt - now,
    }));

    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      entries,
    };
  }

  /**
   * Find oldest cache key
   */
  private findOldestKey(): string | null {
    let oldestKey: string | null = null;
    let oldestTimestamp = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  /**
   * Update configuration
   */
  configure(options: Partial<CacheOptions>): void {
    this.config = { ...this.config, ...options };
  }
}

// Export singleton instance
export const requestCache = new RequestCache();

// Export class for custom instances
export default RequestCache;

